import type { ChannelColorThemeId } from '../channelColorThemes'

export type AdminUser = {
  id: string
  email: string
  fullName: string
}

export type VisualAsset = {
  id: string
  asset_type: 'image' | 'video'
  title: string
  file_url: string
  file_hash: string
  thumbnail_url: string | null
  derivatives: VisualAssetDerivative[]
  duration_seconds: number | null
  mime_type: string | null
  size_bytes: number | null
  status: string
  width: number | null
  height: number | null
  created_at: string
  groups: VisualGroupSummary[]
}

export type VisualAssetDerivative = {
  variant: 'thumb' | 'preview' | 'player'
  url: string
  mime_type: string
  width: number | null
  height: number | null
  size_bytes: number | null
}

export type AudioTrack = {
  id: string
  title: string
  artist: string | null
  style_prompt: string | null
  file_url: string | null
  file_hash: string
  playback_status: AudioPlaybackStatus
  playback_source_kind: AudioPlaybackSourceKind
  playback_format: 'mp3' | null
  playback_bitrate_kbps: number | null
  playback_generated_at: string | null
  playback_error: string | null
  lossless_status: AudioLosslessStatus
  lossless_format: 'flac' | null
  lossless_object_key: string | null
  lossless_storage_backend: AudioLosslessStorageBackend
  lossless_size_bytes: number | null
  lossless_duration_seconds: number | null
  lossless_loudness_lufs: number | null
  lossless_true_peak_db: number | null
  lossless_uploaded_at: string | null
  lossless_error: string | null
  image_url: string | null
  image_derivatives: ImageDerivative[]
  duration_seconds: number | null
  mime_type: string | null
  size_bytes: number | null
  status: string
  created_at: string
  raw_bpm: number | null
  detected_bpm: number | null
  corrected_bpm: number | null
  bpm_correction: 'none' | 'half_time' | 'double_time'
  bpm_correction_reason: string | null
  manual_bpm: number | null
  manual_bpm_source: 'number' | 'tap' | null
  bpm_confidence: number | null
  bpm_analyzed_at: string | null
  effective_bpm: number | null
  effective_bpm_source: 'none' | 'detected' | 'corrected' | 'number' | 'tap'
  groups: AudioGroupSummary[]
  labels: AudioTrackLabelSummary[]
  audio_analysis: AudioAnalysisSummary
  processing_summary: AudioProcessingSummary
  suno_prompt_binding: AudioTrackSunoPromptBindingSummary | null
}

export type AudioPlaybackListTrack = Pick<
  AudioTrack,
  | 'id'
  | 'title'
  | 'artist'
  | 'file_url'
  | 'duration_seconds'
  | 'lossless_status'
  | 'lossless_object_key'
  | 'lossless_storage_backend'
  | 'image_url'
  | 'image_derivatives'
>

export type AudioLosslessStatus = 'missing' | 'processing' | 'ready' | 'failed'
export type AudioLosslessStorageBackend = 'local_filesystem' | 's3'
export type AudioPlaybackStatus = 'missing' | 'processing' | 'ready' | 'failed'
export type AudioPlaybackSourceKind = 'absent' | 'uploaded' | 'generated'
export type AudioTrackLosslessFilter = 'has_flac' | 'missing_flac'

export type SunoPromptBaseSnapshot = {
  id: string
  title: string
  description: string | null
  style_prompt: string
  exclude_prompt: string | null
}

export type AudioTrackSunoPromptBindingSummary = {
  created_at: string
  current_prompt_title: string | null
  matched_prompt_text: string
  prompt_snapshot: SunoPromptBaseSnapshot
  suno_prompt_id: string
}

export type AudioAnalysisStatus = 'never_run' | 'queued' | 'processing' | 'completed' | 'failed'

export type AudioProcessingStatus = 'none' | 'queued' | 'processing' | 'completed' | 'failed'
export type AudioProcessingStepType = 'inspect_audio' | 'generate_audio_image_derivatives' | 'analyze_audio_features' | 'analyze_audio_loudness' | 'analyze_audio_labels' | 'generate_lossless_audio'

export type AudioProcessingSummary = {
  run_id: string | null
  trigger: 'upload' | 'manual_reprocess' | 'manual_label_analysis' | 'backfill' | 'lossless_upload' | null
  status: AudioProcessingStatus
  current_step_type: AudioProcessingStepType | null
  current_step_status: Exclude<AudioProcessingStatus, 'none'> | null
  current_step_error: string | null
  completed_step_count: number
  total_step_count: number
  blocking_completed: boolean
}

export type AudioAnalysisSummary = {
  status: AudioAnalysisStatus
  completed_count: number
  latest_run_id: string | null
  latest_completed_at: string | null
  latest_failed_at: string | null
  pending_job_id: string | null
  last_error: string | null
  pending_suggestion_count: number
  rejected_suggestion_count: number
}

export type LabelCategory = 'genre' | 'mood' | 'use_case' | 'instrument' | 'texture' | 'rhythm' | 'tempo' | 'energy' | 'vocal_presence' | 'language'
export type LabelStatus = 'active' | 'archived'

export type Label = {
  id: string
  category: LabelCategory
  name: string
  slug: string
  description: string | null
  aliases: string[]
  status: LabelStatus
  track_count: number
  created_at: string
  updated_at: string
}

export type AudioTrackLabelSummary = {
  id: string
  category: LabelCategory
  name: string
  slug: string
  source: string
  status: string
}

export type AudioTrackLabel = {
  id: string
  audio_track_id: string
  label_id: string
  source: string
  status: string
  confidence: string | null
  weight: string
  reason: string | null
  label: Pick<Label, 'id' | 'category' | 'name' | 'slug' | 'description' | 'aliases' | 'status'>
  created_at: string
  updated_at: string
}

export type AudioTrackLabelSuggestion = {
  audio_track_id: string | null
  label_id: string
  source: 'prompt_llm' | 'audio_ai'
  status: 'suggested'
  confidence: number
  weight: number
  reason: string
  label: Pick<Label, 'id' | 'category' | 'name' | 'slug' | 'description' | 'aliases' | 'status'>
}

export type AudioTrackLabelSaveInput = {
  labelId: string
  source: 'manual' | 'prompt_llm' | 'audio_local' | 'audio_ai'
  confidence?: number | null
  weight?: number | null
  reason?: string | null
}

export type AudioTrackLabelSuggestionMeta = {
  audio_track_id: string | null
  cache_hit?: boolean
  candidate_label_count: number
  model: string
  provider_request_id: string | null
  usage: {
    promptTokens: number | null
    completionTokens: number | null
    totalTokens: number | null
  }
}

export type AudioTrackLabelSuggestionResult = {
  items: AudioTrackLabelSuggestion[]
  meta: AudioTrackLabelSuggestionMeta
}

export type TrackTitleLanguage = 'en' | 'auto' | 'ru'

export type TrackTitleSuggestion = {
  text: string
}

export type TrackTitleSuggestionResult = {
  items: TrackTitleSuggestion[]
  meta: {
    model: string
    provider_request_id: string | null
    usage: {
      promptTokens: number | null
      completionTokens: number | null
      totalTokens: number | null
    }
  }
}

export type SunoStylePromptSuggestion = {
  title: string
  style_prompt: string
  exclude_prompt: string | null
  explanation: string
}

export type SunoStylePromptSuggestionResult = {
  items: SunoStylePromptSuggestion[]
  history_item: SunoStylePromptHistoryItem | null
  meta: {
    model: string
    provider_request_id: string | null
    usage: {
      promptTokens: number | null
      completionTokens: number | null
      totalTokens: number | null
    }
  }
}

export type SunoPromptMetadataSuggestionResult = {
  title: string
  description: string
  meta: {
    model: string
    provider_request_id: string | null
    usage: {
      promptTokens: number | null
      completionTokens: number | null
      totalTokens: number | null
    }
  }
}

export type SunoStylePromptHistoryItem = {
  id: string
  admin_user_id: string
  description: string
  result_json: SunoStylePromptSuggestion[]
  base_prompt_snapshot: SunoPromptBaseSnapshot | null
  created_at: string
}

export type SunoPrompt = {
  id: string
  title: string
  description: string | null
  style_prompt: string
  exclude_prompt: string | null
  groups: SunoPromptGroupSummary[]
  pool: SunoPromptPoolSummary | null
  pool_variant_count: number
  linked_track_count: number
  created_at: string
  updated_at: string
}

export type SunoPromptFilters = {
  group?: string | undefined
  page?: number | undefined
  pageSize?: number | undefined
  search?: string | undefined
  sortBy?: 'created_at' | 'groups' | 'linked_track_count' | 'title' | 'updated_at' | undefined
  sortDirection?: 'asc' | 'desc' | undefined
}

export type SunoPromptListResult = {
  items: SunoPrompt[]
  page: number
  pageSize: number
  total: number
}

export type SunoPromptGroupSummary = {
  id: string
  name: string
}

export type SunoPromptGroup = {
  id: string
  name: string
  description: string | null
  prompt_count: number
  created_at: string
  updated_at: string
}

export type SunoPromptPoolSummary = {
  id: string
  name: string
  canonical_suno_prompt_id: string
}

export type SunoPromptPoolVariant = {
  id: string
  prompt: SunoPrompt
  sort_order: number
  created_at: string
}

export type SunoPromptPool = {
  id: string
  name: string
  description: string | null
  canonical_suno_prompt_id: string
  canonical_prompt: SunoPrompt
  variants: SunoPromptPoolVariant[]
  created_at: string
  updated_at: string
}

export type SunoPromptSimilarityReason =
  | 'exact_match'
  | 'same_group_unpooled'
  | 'same_style_different_exclude'
  | 'similar_style'

export type SunoPromptSimilarityCandidate = {
  prompt: SunoPrompt
  reason: SunoPromptSimilarityReason
  score: number
  merge_pool: SunoPromptPoolSummary | null
}

export type SunoPromptSimilarityResult = {
  prompt: SunoPrompt
  items: SunoPromptSimilarityCandidate[]
}

export type SunoPromptTrackSummary = {
  id: string
  title: string
  artist: string | null
  style_prompt: string | null
  file_url: string
  duration_seconds: number | null
  status: string
  created_at: string
}

export type SunoPromptTrackBindingSummary = SunoPromptTrackSummary & {
  binding_id: string
  matched_prompt_text: string
  prompt_snapshot: SunoPromptBaseSnapshot
}

export type SunoPromptTrackListResult = {
  prompt: SunoPrompt
  linked: SunoPromptTrackBindingSummary[]
  linkedTotal: number
  matching: SunoPromptTrackSummary[]
  matchingTotal: number
  page: number
  pageSize: number
}

export type AudioTrackLabelStateResult = {
  items: AudioTrackLabel[]
  audio_analysis: AudioAnalysisSummary
}

export type LabelFilters = {
  category?: LabelCategory | undefined
  status?: LabelStatus | undefined
  search?: string | undefined
}

export type ImageDerivative = {
  variant: 'thumb' | 'preview'
  url: string
  mime_type: string
  width: number | null
  height: number | null
  size_bytes: number | null
}

export type AudioGroupSummary = {
  id: string
  name: string
}

export type AudioGroup = {
  id: string
  name: string
  description: string | null
  track_count: number
  channel_count: number
  created_at: string
  updated_at: string
}

export type AudioTrackFilters = {
  group?: string | undefined
  labelIds?: string[] | undefined
  page?: number | undefined
  pageSize?: number | undefined
  search?: string | undefined
  sortBy?: 'created_at' | 'duration' | 'title' | undefined
  sortDirection?: 'asc' | 'desc' | undefined
  sunoPromptBinding?: 'linked' | 'unlinked' | undefined
  lossless?: AudioTrackLosslessFilter | undefined
}

export type AudioTrackListResult = {
  items: AudioTrack[]
  page: number
  pageSize: number
  total: number
}

export type AudioTrackBindingKind = 'label' | 'group'
export type AudioTrackBindingState = 'all' | 'assigned' | 'unassigned' | 'ungrouped'

export type AudioTrackBindingItem = Pick<
  AudioTrack,
  | 'id'
  | 'title'
  | 'artist'
  | 'file_url'
  | 'image_url'
  | 'image_derivatives'
  | 'duration_seconds'
  | 'status'
  | 'created_at'
  | 'effective_bpm'
  | 'effective_bpm_source'
  | 'groups'
  | 'labels'
> & {
  assigned: boolean
}

export type AudioTrackBindingFilters = {
  kind: AudioTrackBindingKind
  targetId: string
  state?: AudioTrackBindingState | undefined
  page?: number | undefined
  pageSize?: number | undefined
  search?: string | undefined
  sortBy?: 'created_at' | 'duration' | 'title' | undefined
  sortDirection?: 'asc' | 'desc' | undefined
}

export type AudioTrackBindingListResult = {
  items: AudioTrackBindingItem[]
  page: number
  pageSize: number
  total: number
  assignedTotal: number
  ungroupedTotal: number
  unassignedTotal: number
}

export type VisualGroupSummary = {
  id: string
  name: string
}

export type VisualGroup = {
  id: string
  name: string
  description: string | null
  asset_count: number
  channel_count: number
  created_at: string
  updated_at: string
}

export type VisualAssetFilters = {
  group?: string | undefined
  type?: 'image' | 'video' | undefined
  search?: string | undefined
  status?: string | undefined
}

export type ChannelGroupSummary = {
  id: string
  name: string
}

export type ChannelTrackSelectionMode = 'groups' | 'labels'
export type ChannelIncludeMatchMode = 'all' | 'any'
export type ChannelLabelRuleMode = 'include' | 'exclude' | 'boost'
export type StreamingFormat = 'mp3'
export type ChannelSourceQuality = 'mp3_only' | 'prefer_lossless' | 'require_lossless'
export type StreamStatus = 'live' | 'stopped' | 'error'

export type ChannelLabelRule = {
  id: string
  label_id: string
  mode: ChannelLabelRuleMode
  weight: string
  label: Pick<Label, 'id' | 'category' | 'name' | 'slug'>
}

export type Channel = {
  id: string
  name: string
  slug: string
  description: string | null
  cover_image_url: string | null
  cover_derivatives: ImageDerivative[]
  color_theme: ChannelColorThemeId
  is_active: boolean
  track_selection_mode: ChannelTrackSelectionMode
  include_match_mode: ChannelIncludeMatchMode
  bpm_min: number | null
  bpm_max: number | null
  sort_order: number
  stream_bitrate: number
  stream_format: StreamingFormat
  source_quality: ChannelSourceQuality
  created_at: string
  updated_at: string
  visual_groups: ChannelGroupSummary[]
  audio_groups: ChannelGroupSummary[]
  label_rules: ChannelLabelRule[]
}

export type StreamStatusResponse = {
  enabled: boolean
  status: StreamStatus
  listeners: number
  current_track: {
    track_id: string
    started_at: string
  } | null
  stream_url: string
}

export type NowPlayingTrack = {
  id: string
  title: string
  artist: string | null
  file_url: string | null
  file_path: string | null
  source_quality: 'mp3' | 'flac'
  image_url: string | null
  image_derivatives: ImageDerivative[]
  duration_seconds: number | null
  started_at: string
}

export type PublicChannel = Pick<
  Channel,
  'id' | 'name' | 'slug' | 'description' | 'cover_image_url' | 'cover_derivatives' | 'color_theme' | 'sort_order'
> & {
  ready_visual_count: number
  ready_audio_count: number
  stream_url: string
}

export type PublicAudioTrack = Omit<Pick<
  AudioTrack,
  'id' | 'title' | 'artist' | 'file_url' | 'image_url' | 'image_derivatives' | 'duration_seconds'
>, 'file_url'> & {
  file_url: string
}

export type ChannelPlaygroundTrack = PublicAudioTrack & {
  effective_bpm: number | null
  effective_bpm_source: 'none' | 'detected' | 'corrected' | 'number' | 'tap'
  match_reasons: {
    audio_groups: Array<{ id: string; name: string }>
    include_labels: Array<Pick<Label, 'id' | 'category' | 'name' | 'slug'>>
    boost_labels: Array<Pick<Label, 'id' | 'category' | 'name' | 'slug'> & { weight: string }>
    bpm: {
      value: number
      source: 'detected' | 'corrected' | 'number' | 'tap'
      min: number | null
      max: number | null
    } | null
  }
}

export type ChannelPlayground = {
  channel: Channel
  tracks: ChannelPlaygroundTrack[]
}

export type PublicChannelVisualAsset = Pick<
  VisualAsset,
  'id' | 'asset_type' | 'title' | 'file_url' | 'thumbnail_url' | 'derivatives' | 'duration_seconds'
>

export type PublicChannelDetail = PublicChannel & {
  visual_assets: PublicChannelVisualAsset[]
}

export type ChannelFilters = {
  search?: string | undefined
}

const resolveApiUrl = (): string => {
  const configuredUrl = import.meta.env.VITE_API_URL

  if (!configuredUrl) {
    return `${window.location.protocol}//${window.location.hostname}:3001`
  }

  try {
    const url = new URL(configuredUrl)

    if (
      window.location.hostname === 'localhost'
      && url.hostname === '127.0.0.1'
    ) {
      url.hostname = 'localhost'
      return url.toString().replace(/\/$/, '')
    }

    return configuredUrl.replace(/\/$/, '')
  } catch {
    return configuredUrl.replace(/\/$/, '')
  }
}

export const apiUrl = resolveApiUrl()

export const resolveMediaUrl = (url: string): string => {
  if (/^(https?:)?\/\//.test(url) || url.startsWith('data:')) {
    return url
  }

  return `${apiUrl}${url.startsWith('/') ? url : `/${url}`}`
}

export const getAudioLosslessUrl = (
  track: Pick<AudioTrack, 'lossless_object_key' | 'lossless_status'> & {
    lossless_storage_backend?: AudioLosslessStorageBackend | null
  }
): string | null => {
  if (track.lossless_status !== 'ready' || !track.lossless_object_key) {
    return null
  }

  if (track.lossless_storage_backend === 's3') {
    return null
  }

  return `/media/${track.lossless_object_key.replace(/^\/+/, '')}`
}

export const getVisualDerivativeUrl = (
  asset: Pick<VisualAsset, 'asset_type' | 'derivatives' | 'file_url' | 'thumbnail_url'>,
  variant: VisualAssetDerivative['variant']
): string => {
  const derivative = asset.derivatives.find((item) => item.variant === variant)

  if (derivative) {
    return derivative.url
  }

  if (variant === 'thumb' && asset.thumbnail_url) {
    return asset.thumbnail_url
  }

  if (variant === 'player') {
    const previewDerivative = asset.derivatives.find((item) => item.variant === 'preview')

    if (previewDerivative) {
      return previewDerivative.url
    }
  }

  return asset.file_url
}

export const getAudioImageUrl = (
  track: Pick<AudioTrack, 'image_derivatives' | 'image_url'>,
  variant: ImageDerivative['variant']
): string | null => {
  const derivative = track.image_derivatives.find((item) => item.variant === variant)

  return derivative?.url ?? track.image_url
}

export const getChannelCoverUrl = (
  channel: Pick<Channel, 'cover_derivatives' | 'cover_image_url'>,
  variant: ImageDerivative['variant']
): string | null => {
  const derivative = channel.cover_derivatives.find((item) => item.variant === variant)

  return derivative?.url ?? channel.cover_image_url
}

const requestJson = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(`${apiUrl}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      ...init?.headers
    }
  })

  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { message?: string }
    throw new Error(payload.message ?? `Request failed with status ${response.status}`)
  }

  if (response.status === 204) {
    return undefined as T
  }

  return (await response.json()) as T
}

export const getHealth = async (): Promise<{ status: string; database: string }> =>
  requestJson('/health')

export const getCurrentAdmin = async (): Promise<AdminUser | null> => {
  const response = await fetch(`${apiUrl}/api/auth/me`, {
    credentials: 'include'
  })

  if (response.status === 401) {
    return null
  }

  if (!response.ok) {
    throw new Error(`Session check failed with status ${response.status}`)
  }

  const payload = (await response.json()) as { user: AdminUser }

  return payload.user
}

export const loginAdmin = async (input: {
  email: string
  password: string
}): Promise<AdminUser> => {
  const payload = await requestJson<{ user: AdminUser }>('/api/auth/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(input)
  })

  return payload.user
}

export const logoutAdmin = async (): Promise<void> => {
  await requestJson('/api/auth/logout', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: '{}'
  })
}

const buildQuery = (params: Record<string, number | string | string[] | undefined>): string => {
  const searchParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item) {
          searchParams.append(key, item)
        }
      })
    } else if (value !== undefined && value !== '') {
      searchParams.set(key, String(value))
    }
  })

  const query = searchParams.toString()

  return query ? `?${query}` : ''
}

export const getVisualAssets = async (
  filters: VisualAssetFilters = {}
): Promise<VisualAsset[]> => {
  const payload = await requestJson<{ items: VisualAsset[] }>(
    `/api/admin/visuals${buildQuery(filters)}`
  )

  return payload.items
}

export const getVisualGroups = async (search?: string): Promise<VisualGroup[]> => {
  const payload = await requestJson<{ items: VisualGroup[] }>(
    `/api/admin/visual-groups${buildQuery({ search })}`
  )

  return payload.items
}

export const getAudioTracks = async (filters: AudioTrackFilters = {}): Promise<AudioTrackListResult> => {
  const payload = await requestJson<AudioTrackListResult>(
    `/api/admin/audio-tracks${buildQuery(filters)}`
  )

  return payload
}

export const getAudioPlaybackList = async (
  filters: AudioTrackFilters = {}
): Promise<AudioPlaybackListTrack[]> => {
  const playbackFilters: AudioTrackFilters = {
    ...filters,
    page: undefined,
    pageSize: undefined
  }
  const payload = await requestJson<{ items: AudioPlaybackListTrack[] }>(
    `/api/admin/audio-tracks/playback-list${buildQuery(playbackFilters)}`
  )

  return payload.items
}

export const getAudioTrackBindings = async (
  filters: AudioTrackBindingFilters
): Promise<AudioTrackBindingListResult> => {
  return requestJson<AudioTrackBindingListResult>(
    `/api/admin/audio-track-bindings${buildQuery(filters)}`
  )
}

export const updateAudioTrackBinding = async (input: {
  kind: AudioTrackBindingKind
  targetId: string
  trackId: string
  assigned: boolean
}): Promise<void> => {
  await requestJson('/api/admin/audio-track-bindings', {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(input)
  })
}

export const getAudioTrack = async (audioTrackId: string): Promise<AudioTrack> => {
  const payload = await requestJson<{ item: AudioTrack }>(
    `/api/admin/audio-tracks/${audioTrackId}`
  )

  return payload.item
}

export const getLabels = async (filters: LabelFilters = {}): Promise<Label[]> => {
  const payload = await requestJson<{ items: Label[] }>(
    `/api/admin/labels${buildQuery(filters)}`
  )

  return payload.items
}

export const getSunoPrompts = async (filters: SunoPromptFilters = {}): Promise<SunoPromptListResult> => {
  const payload = await requestJson<SunoPromptListResult>(
    `/api/admin/suno-prompts${buildQuery(filters)}`
  )

  return payload
}

export const getSunoPromptGroups = async (search?: string): Promise<SunoPromptGroup[]> => {
  const payload = await requestJson<{ items: SunoPromptGroup[] }>(
    `/api/admin/suno-prompt-groups${buildQuery({ search })}`
  )

  return payload.items
}

export const getSunoPromptPools = async (): Promise<SunoPromptPool[]> => {
  const payload = await requestJson<{ items: SunoPromptPool[] }>('/api/admin/suno-prompt-pools')

  return payload.items
}

export const getSimilarSunoPrompts = async (promptId: string): Promise<SunoPromptSimilarityResult> =>
  requestJson<SunoPromptSimilarityResult>(
    `/api/admin/suno-prompts/${encodeURIComponent(promptId)}/similar`
  )

export const createSunoPromptPool = async (input: {
  canonicalPromptId: string
  description?: string | null | undefined
  name?: string | null | undefined
  promptIds: string[]
}): Promise<SunoPromptPool> => {
  const payload = await requestJson<{ item: SunoPromptPool }>('/api/admin/suno-prompt-pools', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(input)
  })

  return payload.item
}

export const updateSunoPromptPool = async (input: {
  canonicalPromptId: string
  description?: string | null | undefined
  id: string
  name: string
  promptIds: string[]
}): Promise<SunoPromptPool> => {
  const payload = await requestJson<{ item: SunoPromptPool }>(
    `/api/admin/suno-prompt-pools/${encodeURIComponent(input.id)}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        canonicalPromptId: input.canonicalPromptId,
        description: input.description,
        name: input.name,
        promptIds: input.promptIds
      })
    }
  )

  return payload.item
}

export const mergeSunoPromptPool = async (input: {
  canonicalPromptId: string
  sourcePoolId: string
  targetPoolId: string
}): Promise<SunoPromptPool> => {
  const payload = await requestJson<{ item: SunoPromptPool }>(
    `/api/admin/suno-prompt-pools/${encodeURIComponent(input.targetPoolId)}/merge`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        canonicalPromptId: input.canonicalPromptId,
        sourcePoolId: input.sourcePoolId
      })
    }
  )

  return payload.item
}

export const deleteSunoPromptPool = async (poolId: string): Promise<void> => {
  await requestJson(`/api/admin/suno-prompt-pools/${encodeURIComponent(poolId)}`, {
    method: 'DELETE'
  })
}

export const findMatchingSunoPrompt = async (stylePrompt: string, excludePrompt?: string | null | undefined): Promise<SunoPrompt | null> => {
  const payload = await requestJson<{ item: SunoPrompt | null }>(
    `/api/admin/suno-prompts/match${buildQuery({ stylePrompt, excludePrompt: excludePrompt || undefined })}`
  )

  return payload.item
}

export const getSunoPrompt = async (promptId: string): Promise<SunoPrompt> => {
  const payload = await requestJson<{ item: SunoPrompt }>(`/api/admin/suno-prompts/${promptId}`)

  return payload.item
}

export const getSunoPromptTracks = async (
  promptId: string,
  filters: { page?: number | undefined; pageSize?: number | undefined; search?: string | undefined } = {}
): Promise<SunoPromptTrackListResult> =>
  requestJson<SunoPromptTrackListResult>(
    `/api/admin/suno-prompts/${promptId}/tracks${buildQuery(filters)}`
  )

export const linkSunoPromptTracks = async (input: {
  linkAllMatching?: boolean
  promptId: string
  search?: string | undefined
  trackIds?: string[] | undefined
}): Promise<{ linkedCount: number; skippedCount: number }> =>
  requestJson<{ linkedCount: number; skippedCount: number }>(
    `/api/admin/suno-prompts/${input.promptId}/tracks/link`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        linkAllMatching: input.linkAllMatching,
        search: input.search,
        trackIds: input.trackIds
      })
    }
  )

export const unlinkAudioTrackSunoPrompt = async (trackId: string): Promise<void> => {
  const response = await fetch(`${apiUrl}/api/admin/audio-tracks/${trackId}/suno-prompt-binding`, {
    credentials: 'include',
    method: 'DELETE'
  })

  if (!response.ok && response.status !== 404) {
    const payload = (await response.json().catch(() => ({}))) as { message?: string }
    throw new Error(payload.message ?? `Request failed with status ${response.status}`)
  }
}

export const createSunoPrompt = async (input: {
  title: string
  description: string | null
  sunoGroupIds?: string[] | undefined
  style_prompt: string
  exclude_prompt: string | null
}): Promise<SunoPrompt> => {
  const payload = await requestJson<{ item: SunoPrompt }>('/api/admin/suno-prompts', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(input)
  })

  return payload.item
}

export const updateSunoPrompt = async (input: {
  promptId: string
  title: string
  description: string | null
  sunoGroupIds?: string[] | undefined
  style_prompt: string
  exclude_prompt: string | null
}): Promise<SunoPrompt> => {
  const payload = await requestJson<{ item: SunoPrompt }>(`/api/admin/suno-prompts/${input.promptId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      title: input.title,
      description: input.description,
      sunoGroupIds: input.sunoGroupIds,
      style_prompt: input.style_prompt,
      exclude_prompt: input.exclude_prompt
    })
  })

  return payload.item
}

export const createSunoPromptGroup = async (input: {
  name: string
  description: string | null
}): Promise<SunoPromptGroup> => {
  const payload = await requestJson<{ item: SunoPromptGroup }>('/api/admin/suno-prompt-groups', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(input)
  })

  return payload.item
}

export const updateSunoPromptGroup = async (input: {
  id: string
  name: string
  description: string | null
}): Promise<SunoPromptGroup> => {
  const payload = await requestJson<{ item: SunoPromptGroup }>(
    `/api/admin/suno-prompt-groups/${input.id}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: input.name,
        description: input.description
      })
    }
  )

  return payload.item
}

export const deleteSunoPromptGroup = async (groupId: string): Promise<void> => {
  await requestJson(`/api/admin/suno-prompt-groups/${groupId}`, {
    method: 'DELETE'
  })
}

export const deleteSunoPrompt = async (promptId: string): Promise<SunoPrompt> => {
  const payload = await requestJson<{ item: SunoPrompt }>(`/api/admin/suno-prompts/${promptId}`, {
    method: 'DELETE'
  })

  return payload.item
}

export const createLabel = async (input: {
  category: LabelCategory
  name: string
  slug: string
  description: string | null
  aliases: string[]
}): Promise<Label> => {
  const payload = await requestJson<{ item: Label }>('/api/admin/labels', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(input)
  })

  return payload.item
}

export const updateLabel = async (input: {
  id: string
  category: LabelCategory
  name: string
  slug: string
  description: string | null
  aliases: string[]
  status: LabelStatus
}): Promise<Label> => {
  const payload = await requestJson<{ item: Label }>(`/api/admin/labels/${input.id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(input)
  })

  return payload.item
}

export const archiveLabel = async (labelId: string): Promise<Label> => {
  const payload = await requestJson<{ item: Label }>(`/api/admin/labels/${labelId}/archive`, {
    method: 'POST'
  })

  return payload.item
}

export type VisualUploadSkippedFile = {
  filename: string
  duplicateOfId?: string
  reason: string
}

export type VisualUploadResult = {
  items: VisualAsset[]
  skipped: VisualUploadSkippedFile[]
}

export const uploadVisualFile = async (
  file: File,
  visualGroupIds: string[] = [],
  onProgress?: (progress: number) => void
): Promise<VisualUploadResult> => {
  const formData = new FormData()

  visualGroupIds.forEach((id) => formData.append('visualGroupIds', id))
  formData.append('file', file)

  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()

    request.open('POST', `${apiUrl}/api/admin/visuals/upload`)
    request.withCredentials = true

    request.upload.onprogress = (event) => {
      if (!event.lengthComputable) {
        return
      }

      onProgress?.(Math.round((event.loaded / event.total) * 100))
    }

    request.onload = () => {
      const payload = JSON.parse(request.responseText || '{}') as Partial<VisualUploadResult> & {
        message?: string
      }

      if (request.status < 200 || request.status >= 300) {
        reject(new Error(payload.message ?? `Request failed with status ${request.status}`))
        return
      }

      resolve({
        items: payload.items ?? [],
        skipped: payload.skipped ?? []
      })
    }

    request.onerror = () => reject(new Error('Upload failed'))
    request.onabort = () => reject(new Error('Upload cancelled'))

    request.send(formData)
  })
}

export const updateVisualAsset = async (input: {
  id: string
  title: string
}): Promise<VisualAsset> => {
  const payload = await requestJson<{ item: VisualAsset }>(`/api/admin/visuals/${input.id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      title: input.title
    })
  })

  return payload.item
}

export const updateVisualAssetGroups = async (
  visualAssetId: string,
  visualGroupIds: string[]
): Promise<VisualAsset> => {
  const payload = await requestJson<{ item: VisualAsset }>(
    `/api/admin/visuals/${visualAssetId}/groups`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ visualGroupIds })
    }
  )

  return payload.item
}

export const deleteVisualAsset = async (visualAssetId: string): Promise<void> => {
  await requestJson(`/api/admin/visuals/${visualAssetId}`, {
    method: 'DELETE'
  })
}

export const createVisualGroup = async (input: {
  name: string
  description: string | null
}): Promise<VisualGroup> => {
  const payload = await requestJson<{ item: VisualGroup }>('/api/admin/visual-groups', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(input)
  })

  return payload.item
}

export const updateVisualGroup = async (input: {
  id: string
  name: string
  description: string | null
}): Promise<VisualGroup> => {
  const payload = await requestJson<{ item: VisualGroup }>(
    `/api/admin/visual-groups/${input.id}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: input.name,
        description: input.description
      })
    }
  )

  return payload.item
}

export const deleteVisualGroup = async (visualGroupId: string): Promise<void> => {
  await requestJson(`/api/admin/visual-groups/${visualGroupId}`, {
    method: 'DELETE'
  })
}

export const uploadAudioFiles = async (
  files: FileList,
  audioGroupIds: string[] = []
): Promise<AudioTrack[]> => {
  const formData = new FormData()

  audioGroupIds.forEach((id) => formData.append('audioGroupIds', id))
  Array.from(files).forEach((file) => formData.append('files', file))

  const payload = await requestJson<{ items: AudioTrack[] }>('/api/admin/audio-tracks/upload', {
    method: 'POST',
    body: formData
  })

  return payload.items
}

export type AudioUploadSkippedFile = {
  filename: string
  duplicateOfId?: string
  reason: string
}

export type AudioUploadMatchedFile = {
  filename: string
  track: AudioTrack
}

export type AudioUploadResult = {
  items: AudioTrack[]
  matched: AudioUploadMatchedFile[]
  skipped: AudioUploadSkippedFile[]
}

export const uploadAudioFile = async (
  file: File,
  audioGroupIds: string[] = [],
  stylePrompt?: string | null,
  sunoPromptId?: string | null,
  onProgress?: (progress: number) => void
): Promise<AudioUploadResult> => {
  const formData = new FormData()

  audioGroupIds.forEach((id) => formData.append('audioGroupIds', id))
  if (stylePrompt?.trim()) {
    formData.append('stylePrompt', stylePrompt.trim())
  }
  if (sunoPromptId) {
    formData.append('sunoPromptId', sunoPromptId)
  }
  formData.append('file', file)

  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()

    request.open('POST', `${apiUrl}/api/admin/audio-tracks/upload`)
    request.withCredentials = true

    request.upload.onprogress = (event) => {
      if (!event.lengthComputable) {
        return
      }

      onProgress?.(Math.round((event.loaded / event.total) * 100))
    }

    request.onload = () => {
      const payload = JSON.parse(request.responseText || '{}') as Partial<AudioUploadResult> & {
        message?: string
      }

      if (request.status < 200 || request.status >= 300) {
        reject(new Error(payload.message ?? `Request failed with status ${request.status}`))
        return
      }

      resolve({
        items: payload.items ?? [],
        matched: payload.matched ?? [],
        skipped: payload.skipped ?? []
      })
    }

    request.onerror = () => reject(new Error('Upload failed'))
    request.onabort = () => reject(new Error('Upload cancelled'))

    request.send(formData)
  })
}

export const updateAudioTrack = async (input: {
  id: string
  title: string
  artist: string | null
  style_prompt: string | null
}): Promise<AudioTrack> => {
  const payload = await requestJson<{ item: AudioTrack }>(`/api/admin/audio-tracks/${input.id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      title: input.title,
      artist: input.artist,
      style_prompt: input.style_prompt
    })
  })

  return payload.item
}

export const updateAudioTrackBpm = async (input: {
  id: string
  bpm: number
  source: 'number' | 'tap'
}): Promise<AudioTrack> => {
  const payload = await requestJson<{ item: AudioTrack }>(`/api/admin/audio-tracks/${input.id}/bpm`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      bpm: input.bpm,
      source: input.source
    })
  })

  return payload.item
}

export const clearAudioTrackBpm = async (audioTrackId: string): Promise<AudioTrack> => {
  const payload = await requestJson<{ item: AudioTrack }>(
    `/api/admin/audio-tracks/${audioTrackId}/bpm`,
    {
      method: 'DELETE'
    }
  )

  return payload.item
}

export const getAudioTrackLabels = async (audioTrackId: string): Promise<AudioTrackLabelStateResult> => {
  const payload = await requestJson<AudioTrackLabelStateResult>(
    `/api/admin/audio-tracks/${audioTrackId}/labels`
  )

  return payload
}

export const updateAudioTrackLabels = async (
  audioTrackId: string,
  labels: AudioTrackLabelSaveInput[]
): Promise<AudioTrackLabel[]> => {
  const payload = await requestJson<{ items: AudioTrackLabel[] }>(
    `/api/admin/audio-tracks/${audioTrackId}/labels`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ labels })
    }
  )

  return payload.items
}

export const acceptAudioTrackLabel = async (
  audioTrackId: string,
  assignmentId: string
): Promise<AudioTrackLabel> => {
  const payload = await requestJson<{ item: AudioTrackLabel }>(
    `/api/admin/audio-tracks/${audioTrackId}/labels/${assignmentId}/accept`,
    {
      method: 'POST'
    }
  )

  return payload.item
}

export const rejectAudioTrackLabel = async (
  audioTrackId: string,
  assignmentId: string
): Promise<AudioTrackLabel> => {
  const payload = await requestJson<{ item: AudioTrackLabel }>(
    `/api/admin/audio-tracks/${audioTrackId}/labels/${assignmentId}/reject`,
    {
      method: 'POST'
    }
  )

  return payload.item
}

export const analyzeAudioTrackLabels = async (audioTrackId: string): Promise<{ audio_analysis: AudioAnalysisSummary }> => {
  return requestJson(`/api/admin/audio-tracks/${audioTrackId}/analyze-audio-labels`, {
    method: 'POST'
  })
}

export const suggestAudioTrackLabels = async (input: {
  audioTrackId: string
  style_prompt: string
}): Promise<AudioTrackLabelSuggestionResult> =>
  requestJson<AudioTrackLabelSuggestionResult>(
    `/api/admin/audio-tracks/${input.audioTrackId}/label-suggestions`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ style_prompt: input.style_prompt })
    }
  )

export const suggestAudioLabelsFromPrompt = async (input: {
  style_prompt: string
}): Promise<AudioTrackLabelSuggestionResult> =>
  requestJson<AudioTrackLabelSuggestionResult>(
    '/api/admin/audio-tracks/label-suggestions',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ style_prompt: input.style_prompt })
    }
  )

export const suggestTrackTitles = async (input: {
  artist?: string | null
  language: TrackTitleLanguage
  style_prompt: string
}): Promise<TrackTitleSuggestionResult> =>
  requestJson<TrackTitleSuggestionResult>(
    '/api/admin/creative-tools/track-title-suggestions',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(input)
    }
  )

export const suggestSunoStylePrompts = async (input: {
  description: string
  base_prompt_id?: string | null | undefined
  base_prompt?: SunoPromptBaseSnapshot | null | undefined
}): Promise<SunoStylePromptSuggestionResult> =>
  requestJson<SunoStylePromptSuggestionResult>(
    '/api/admin/creative-tools/suno-style-prompts',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(input)
    }
  )

export const suggestSunoPromptMetadata = async (input: {
  style_prompt: string
  exclude_prompt?: string | null | undefined
}): Promise<SunoPromptMetadataSuggestionResult> =>
  requestJson<SunoPromptMetadataSuggestionResult>(
    '/api/admin/creative-tools/suno-prompt-metadata',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(input)
    }
  )

export const getSunoStylePromptHistory = async (): Promise<SunoStylePromptHistoryItem[]> => {
  const payload = await requestJson<{ items: SunoStylePromptHistoryItem[] }>(
    '/api/admin/creative-tools/suno-style-prompts/history'
  )

  return payload.items
}

export const deleteSunoStylePromptHistoryItem = async (historyId: string): Promise<void> => {
  await requestJson(`/api/admin/creative-tools/suno-style-prompts/history/${historyId}`, {
    method: 'DELETE'
  })
}

export const updateAudioTrackGroups = async (
  audioTrackId: string,
  audioGroupIds: string[]
): Promise<AudioTrack> => {
  const payload = await requestJson<{ item: AudioTrack }>(
    `/api/admin/audio-tracks/${audioTrackId}/groups`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ audioGroupIds })
    }
  )

  return payload.item
}

export const uploadAudioTrackImage = async (audioTrackId: string, file: File): Promise<AudioTrack> => {
  const formData = new FormData()
  formData.append('file', file)

  const payload = await requestJson<{ item: AudioTrack }>(
    `/api/admin/audio-tracks/${audioTrackId}/image`,
    {
      method: 'POST',
      body: formData
    }
  )

  return payload.item
}

export const uploadAudioTrackLosslessSource = async (audioTrackId: string, file: File): Promise<AudioTrack> => {
  const formData = new FormData()
  formData.append('file', file)

  const payload = await requestJson<{ item: AudioTrack }>(
    `/api/admin/audio-tracks/${audioTrackId}/lossless-source`,
    {
      method: 'POST',
      body: formData
    }
  )

  return payload.item
}

export const deleteAudioTrackLosslessSource = async (audioTrackId: string): Promise<AudioTrack> => {
  const payload = await requestJson<{ item: AudioTrack }>(
    `/api/admin/audio-tracks/${audioTrackId}/lossless-source`,
    {
      method: 'DELETE'
    }
  )

  return payload.item
}

export const deleteAudioTrackImage = async (audioTrackId: string): Promise<AudioTrack> => {
  const payload = await requestJson<{ item: AudioTrack }>(
    `/api/admin/audio-tracks/${audioTrackId}/image`,
    {
      method: 'DELETE'
    }
  )

  return payload.item
}

export const reprocessAudioTrack = async (audioTrackId: string): Promise<AudioTrack> => {
  const payload = await requestJson<{ item: AudioTrack }>(
    `/api/admin/audio-tracks/${audioTrackId}/reprocess`,
    {
      method: 'POST'
    }
  )

  return payload.item
}

export const deleteAudioTrack = async (audioTrackId: string): Promise<void> => {
  await requestJson(`/api/admin/audio-tracks/${audioTrackId}`, {
    method: 'DELETE'
  })
}

export const getAudioGroups = async (search?: string): Promise<AudioGroup[]> => {
  const payload = await requestJson<{ items: AudioGroup[] }>(
    `/api/admin/audio-groups${buildQuery({ search })}`
  )

  return payload.items
}

export const createAudioGroup = async (input: {
  name: string
  description: string | null
}): Promise<AudioGroup> => {
  const payload = await requestJson<{ item: AudioGroup }>('/api/admin/audio-groups', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(input)
  })

  return payload.item
}

export const updateAudioGroup = async (input: {
  id: string
  name: string
  description: string | null
}): Promise<AudioGroup> => {
  const payload = await requestJson<{ item: AudioGroup }>(
    `/api/admin/audio-groups/${input.id}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: input.name,
        description: input.description
      })
    }
  )

  return payload.item
}

export const deleteAudioGroup = async (audioGroupId: string): Promise<void> => {
  await requestJson(`/api/admin/audio-groups/${audioGroupId}`, {
    method: 'DELETE'
  })
}

export const getChannels = async (filters: ChannelFilters = {}): Promise<Channel[]> => {
  const payload = await requestJson<{ items: Channel[] }>(
    `/api/admin/channels${buildQuery(filters)}`
  )

  return payload.items
}

export const getPublicChannels = async (): Promise<PublicChannel[]> => {
  const payload = await requestJson<{ items: PublicChannel[] }>('/api/public/channels')

  return payload.items
}

export const getPublicChannel = async (channelSlug: string): Promise<PublicChannelDetail> => {
  const payload = await requestJson<{ item: PublicChannelDetail }>(
    `/api/public/channels/${encodeURIComponent(channelSlug)}`
  )

  return payload.item
}

export const getChannelPlayground = async (channelId: string): Promise<ChannelPlayground> =>
  requestJson<ChannelPlayground>(
    `/api/admin/channels/${encodeURIComponent(channelId)}/playground`
  )

export const getNextChannelPlaygroundAudioTrack = async (
  channelId: string,
  input: {
    excludeTrackIds: string[]
    currentTrackId?: string | null | undefined
  }
): Promise<PublicAudioTrack> => {
  const payload = await requestJson<{ track: PublicAudioTrack }>(
    `/api/admin/channels/${encodeURIComponent(channelId)}/playground/audio/next`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(input)
    }
  )

  return payload.track
}

export const getNextPublicChannelAudioTrack = async (
  channelSlug: string,
  input: {
    excludeTrackIds: string[]
    currentTrackId?: string | null | undefined
  }
): Promise<PublicAudioTrack> => {
  const payload = await requestJson<{ track: PublicAudioTrack }>(
    `/api/public/channels/${encodeURIComponent(channelSlug)}/audio/next`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(input)
    }
  )

  return payload.track
}

export const getPublicChannelAudioQueue = async (
  channelSlug: string,
  input: {
    excludeTrackIds: string[]
    currentTrackId?: string | null | undefined
    limit?: number | undefined
  }
): Promise<PublicAudioTrack[]> => {
  const payload = await requestJson<{ tracks: PublicAudioTrack[] }>(
    `/api/public/channels/${encodeURIComponent(channelSlug)}/audio/queue`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(input)
    }
  )

  return payload.tracks
}

export const createChannel = async (input: {
  name: string
  slug: string
  description: string | null
  color_theme: ChannelColorThemeId
  is_active: boolean
  track_selection_mode: ChannelTrackSelectionMode
  include_match_mode: ChannelIncludeMatchMode
  bpm_min: number | null
  bpm_max: number | null
  sort_order: number
  stream_bitrate: number
  stream_format: StreamingFormat
  source_quality?: ChannelSourceQuality
}): Promise<Channel> => {
  const payload = await requestJson<{ item: Channel }>('/api/admin/channels', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(input)
  })

  return payload.item
}

export const updateChannel = async (input: {
  id: string
  name: string
  slug: string
  description: string | null
  color_theme: ChannelColorThemeId
  is_active: boolean
  track_selection_mode: ChannelTrackSelectionMode
  include_match_mode: ChannelIncludeMatchMode
  bpm_min: number | null
  bpm_max: number | null
  sort_order: number
  stream_bitrate: number
  stream_format: StreamingFormat
  source_quality?: ChannelSourceQuality
}): Promise<Channel> => {
  const payload = await requestJson<{ item: Channel }>(
    `/api/admin/channels/${input.id}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: input.name,
        slug: input.slug,
        description: input.description,
        color_theme: input.color_theme,
        is_active: input.is_active,
        track_selection_mode: input.track_selection_mode,
        include_match_mode: input.include_match_mode,
        bpm_min: input.bpm_min,
        bpm_max: input.bpm_max,
        sort_order: input.sort_order,
        stream_bitrate: input.stream_bitrate,
        stream_format: input.stream_format,
        source_quality: input.source_quality
      })
    }
  )

  return payload.item
}

export const updateChannelLabelRules = async (
  channelId: string,
  rules: Array<{
    labelId: string
    mode: ChannelLabelRuleMode
    weight?: number
  }>
): Promise<Channel> => {
  const payload = await requestJson<{ item: Channel }>(
    `/api/admin/channels/${channelId}/label-rules`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ rules })
    }
  )

  return payload.item
}

export const deleteChannel = async (channelId: string): Promise<void> => {
  await requestJson(`/api/admin/channels/${channelId}`, {
    method: 'DELETE'
  })
}

export const updateChannelGroups = async (
  channelId: string,
  visualGroupIds: string[],
  audioGroupIds: string[]
): Promise<Channel> => {
  const payload = await requestJson<{ item: Channel }>(
    `/api/admin/channels/${channelId}/groups`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ visualGroupIds, audioGroupIds })
    }
  )

  return payload.item
}

export const uploadChannelCover = async (channelId: string, file: File): Promise<Channel> => {
  const formData = new FormData()
  formData.append('file', file)

  const payload = await requestJson<{ item: Channel }>(
    `/api/admin/channels/${channelId}/cover`,
    {
      method: 'POST',
      body: formData
    }
  )

  return payload.item
}

export const getChannelStreamStatus = async (channelId: string): Promise<StreamStatusResponse> => {
  return requestJson<StreamStatusResponse>(`/api/admin/channels/${channelId}/stream/status`)
}

export const startChannelStream = async (channelId: string): Promise<{ success: boolean; message: string }> => {
  return requestJson<{ success: boolean; message: string }>(
    `/api/admin/channels/${channelId}/stream/start`,
    { method: 'POST' }
  )
}

export const stopChannelStream = async (channelId: string): Promise<{ success: boolean; message: string }> => {
  return requestJson<{ success: boolean; message: string }>(
    `/api/admin/channels/${channelId}/stream/stop`,
    { method: 'POST' }
  )
}

export const getNowPlayingTrack = async (channelSlug: string): Promise<NowPlayingTrack> => {
  return requestJson<{ track: NowPlayingTrack }>(`/api/streaming/now-playing/${channelSlug}`)
    .then(res => res.track)
}

export const getNowPlayingTrackOrNull = async (channelSlug: string): Promise<NowPlayingTrack | null> => {
  const response = await fetch(`${apiUrl}/api/streaming/now-playing/${encodeURIComponent(channelSlug)}`, {
    credentials: 'include'
  })

  if (response.status === 404) {
    return null
  }

  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { message?: string }
    throw new Error(payload.message ?? `Request failed with status ${response.status}`)
  }

  const payload = (await response.json()) as { track: NowPlayingTrack }

  return payload.track
}

export const formatBytes = (value: number | null): string => {
  if (!value) {
    return '0 B'
  }

  const units = ['B', 'KB', 'MB', 'GB']
  const exponent = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1)
  const amount = value / 1024 ** exponent

  return `${amount.toFixed(amount >= 10 ? 0 : 1)} ${units[exponent]}`
}
